/* eslint-disable react-refresh/only-export-components */
/**
 * AIAssistantTab — Unified Conversational AI Assistant
 *
 * Routes help-centre platform questions, manufacturer manuals & AS/NZS standards,
 * troubleshooting flows, source doc links, brand chips, and role/page starters.
 */

import React, { useRef, useEffect, useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    IconButton,
    Chip,
    CircularProgress,
    Alert,
    Button,
    alpha,
    useTheme,
    InputAdornment,
    Tooltip,
} from '@mui/material';
import {
    Send as SendIcon,
    AutoAwesome as AIIcon,
    Person as PersonIcon,
    Forum as ContactIcon,
    DeleteOutline as ClearIcon,
    ThumbUp as ThumbUpIcon,
    ThumbUpOutlined as ThumbUpOutlinedIcon,
    ThumbDown as ThumbDownIcon,
    ThumbDownOutlined as ThumbDownOutlinedIcon,
    OpenInNew as OpenInNewIcon,
    InfoOutlined as InfoOutlinedIcon,
    ErrorOutline as ErrorOutlineIcon,
    WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import { useAnythingLLM, type ChatMessage } from '../hooks/useAnythingLLM';
import { useSupportHub } from '../SupportHubContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useCustomerExpertAvailability } from '../../help-chat/useCustomerExpertAvailability';
import { EquipmentBrandChip } from '../EquipmentBrandChip';
import { SourceChips } from '../../expert-chat/SourceChips';
import { TroubleshootingFlowCard } from '../../expert-chat/TroubleshootingFlowCard';
import { useTroubleshootingFlow } from '../../expert-chat/useTroubleshootingFlow';
import ReactMarkdown from 'react-markdown';
import { isSafeAttachmentUrl } from '../../../utils/isSafeAttachmentUrl';
import { Link, useLocation, useNavigate } from 'react-router-dom';

function useSafeAuth() {
    try {
        const auth = useAuth();
        return {
            user: auth.user,
            hasPermission: auth.hasPermission ?? (() => false),
        };
    } catch {
        return { user: null, hasPermission: () => false };
    }
}

interface RoleFields {
    is_platform_admin?: boolean | null;
    active_role?: string | null;
    account_type?: string | null;
    trade_company_id?: string | null;
}

export function resolveUserRole(user: RoleFields | null | undefined): 'admin' | 'trade' | 'client' {
    if (!user) return 'client';
    if (user.is_platform_admin || user.active_role === 'platform' || user.account_type === 'platform') {
        return 'admin';
    }
    // A dual-role user's active role decides, so acting as a customer gets the client starters.
    if (user.active_role === 'customer') return 'client';
    if (user.active_role === 'trade_company' || user.trade_company_id || user.account_type === 'trade_company') {
        return 'trade';
    }
    return 'client';
}

export const ROLE_STARTERS: Record<'admin' | 'trade' | 'client', string[]> = {
    trade: [
        'What does fault F18 mean on a GoodWe ET?',
        'How do I set an export limit?',
        'How do I accept a job?',
    ],
    client: [
        'What does the red light on my battery mean?',
        'How do I pay my deposit?',
        'How do I message my installer?',
    ],
    admin: [
        'What does fault F18 mean on a GoodWe ET?',
        'How do I set an export limit?',
        'What does the red light on my battery mean?',
        'How do I pay my deposit?',
    ],
};

export function isProductIshQuestion(text: string): boolean {
    if (!text) return false;
    // Error codes like F18, E23, 532, fault code 103, alarm 5, etc.
    if (/\b(?:(?:ERR(?:OR)?|FLT|FAULT|ALM|ALARM|WARN(?:ING)?|WRN)(?:\s+(?:code|num(?:ber)?))?[-_:\s]?[A-Z]?|[EFWA][-_]?)\d{1,5}[A-Z]?\b/i.test(text)) {
        return true;
    }
    // Generic fault/troubleshooting terms
    if (/\b(?:fault|troubleshoot|ground fault|earth fault|relay fail|isolation resistance)\b/i.test(text)) {
        return true;
    }
    // Inverter / battery brand names
    const brands = [
        'goodwe', 'sungrow', 'fronius', 'enphase', 'tesla', 'solaredge',
        'sigenergy', 'huawei', 'byd', 'sma', 'victron', 'growatt', 'fox',
        'fimer', 'abb', 'selectronic', 'powerwall', 'alphaess', 'solis',
    ];
    const lower = text.toLowerCase();
    if (brands.some((b) => lower.includes(b))) {
        return true;
    }
    // Hardware / technical / standards keywords
    const techTerms = [
        'inverter', 'battery', 'solar', 'panel', 'manual', 'standard', 'as/nzs',
        '5033', '5139', '3000', '4777', 'isolation', 'insulation', 'firmware',
        'commission', 'export limit', 'grid', 'pv', 'dc isolator', 'mppt', 'ac coupled',
    ];
    return techTerms.some((t) => lower.includes(t));
}

export const StagedWaitingIndicator: React.FC<{ question?: string }> = ({ question = '' }) => {
    const [phase, setPhase] = useState<'help' | 'product'>('help');

    useEffect(() => {
        if (!question || !isProductIshQuestion(question)) return;
        const timer = setTimeout(() => {
            setPhase('product');
        }, 2500);
        return () => clearTimeout(timer);
    }, [question]);

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }} data-testid="staged-waiting-indicator">
            <CircularProgress size={14} color="secondary" />
            <Typography variant="caption" color="text.secondary">
                {phase === 'product' ? 'Checking manuals & standards…' : 'Searching the help centre…'}
            </Typography>
        </Box>
    );
};

const KNOWN_IN_APP_PREFIXES = [
    '/dashboard',
    '/settings',
    '/admin',
    '/support',
    '/service',
    '/quote',
    '/news',
    '/profile',
    '/documents',
    '/notifications',
    '/crm',
    '/crews',
    '/variations',
    '/invoices',
    '/login',
    '/signup',
    '/reviews',
    '/schedule',
    '/reschedule',
    '/warranty',
    '/compliance',
    '/jobs',
    '/messages',
    '/customers',
];

export function isInAppHref(href: string): boolean {
    if (!href || typeof href !== 'string') return false;
    if (!/^\/[^/\\]/.test(href)) return false;
    if (href.includes('\\')) return false;
    if (href === '/library?article=' || href.startsWith('/library?article=')) return true;
    return KNOWN_IN_APP_PREFIXES.some(
        (prefix) => href === prefix || href.startsWith(`${prefix}/`) || href.startsWith(`${prefix}?`),
    );
}

const HELP_CHAT_EXTERNAL_LINK_HOSTS = new Set(['tradeconnector.com.au', 'www.tradeconnector.com.au']);

export function isAllowedExternalHelpHref(href: string): boolean {
    if (!href.startsWith('https://')) return false;
    try {
        const url = new URL(href);
        return (
            url.protocol === 'https:' &&
            !url.username &&
            !url.password &&
            !url.port &&
            HELP_CHAT_EXTERNAL_LINK_HOSTS.has(url.hostname.toLowerCase())
        );
    } catch {
        return false;
    }
}

const AIAssistantTab: React.FC = () => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const navigate = useNavigate();
    const location = useLocation();
    const { user, hasPermission } = useSafeAuth();
    const userRole = resolveUserRole(user);
    const starters = ROLE_STARTERS[userRole];

    const {
        setActiveTab,
        closePanel,
        setContactPrefill,
        pendingQuestion,
        setPendingQuestion,
    } = useSupportHub();

    const {
        messages,
        isLoading,
        expertSessionId,
        equipmentScope,
        setEquipmentScope,
        flowOffer,
        setFlowOffers,
        dismissFlow,
        sendMessage,
        clearMessages,
        sendFeedback,
    } = useAnythingLLM();

    const hasClientExpertAction = messages.some((m) =>
        m.actions?.some((a) => a.type === 'expert_chat' && a.audience === 'CLIENT'),
    );
    const { data: isCustomerExpertAvailable } = useCustomerExpertAvailability({
        enabled: hasClientExpertAction,
    });

    const flow = useTroubleshootingFlow({ sessionId: expertSessionId });

    const [inputValue, setInputValue] = useState('');
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const handledPendingQuestionIdRef = useRef<string | null>(null);

    // Auto-scroll to bottom on new messages
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Handle pending question from "Ask about this page" or HomeTab
    useEffect(() => {
        if (!pendingQuestion) return;
        if (isLoading) return;

        const questionId =
            pendingQuestion.id || `${pendingQuestion.question}::${pendingQuestion.contextId ?? ''}`;
        if (handledPendingQuestionIdRef.current === questionId) {
            return;
        }
        handledPendingQuestionIdRef.current = questionId;

        const { question, contextId } = pendingQuestion;
        setPendingQuestion?.(null);
        sendMessage(question, contextId);
    }, [pendingQuestion, isLoading, sendMessage, setPendingQuestion]);

    const handleSend = () => {
        if (!inputValue.trim() || isLoading) return;
        sendMessage(inputValue);
        setInputValue('');
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleSuggestionClick = (suggestion: string) => {
        sendMessage(suggestion);
    };

    const handleFeedbackClick = async (messageId: string, rating: 1 | -1) => {
        try {
            await sendFeedback(messageId, rating);
        } catch (err) {
            console.error('Failed to submit feedback:', err);
        }
    };

    const renderMessage = (msg: ChatMessage) => {
        const isUser = msg.role === 'user';
        const isV2Assistant = !isUser && msg.version === 2;

        return (
            <Box
                key={msg.id}
                sx={{
                    display: 'flex',
                    gap: 1,
                    mb: 1.5,
                    flexDirection: isUser ? 'row-reverse' : 'row',
                    alignItems: 'flex-start',
                }}
            >
                {/* Avatar */}
                <Box
                    sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        bgcolor: isUser
                            ? alpha(theme.palette.primary.main, 0.15)
                            : alpha(theme.palette.secondary.main, 0.15),
                        color: isUser
                            ? theme.palette.primary.main
                            : theme.palette.secondary.main,
                    }}
                >
                    {isUser ? (
                        <PersonIcon sx={{ fontSize: '0.9rem' }} />
                    ) : (
                        <AIIcon sx={{ fontSize: '0.9rem' }} />
                    )}
                </Box>

                {/* Bubble */}
                <Box
                    sx={{
                        maxWidth: '85%',
                        p: 1.5,
                        borderRadius: isUser
                            ? '12px 12px 4px 12px'
                            : '12px 12px 12px 4px',
                        bgcolor: isUser
                            ? alpha(theme.palette.primary.main, 0.1)
                            : isDark
                                ? alpha('#fff', 0.05)
                                : alpha('#000', 0.03),
                        border: `1px solid ${
                            isUser
                                ? alpha(theme.palette.primary.main, 0.15)
                                : theme.palette.divider
                        }`,
                    }}
                >
                    {/* Mode label per answer */}
                    {!isUser && !msg.isLoading && (
                        <Typography
                            variant="caption"
                            data-testid="assistant-mode-label"
                            sx={{
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                                fontSize: '0.65rem',
                                color: msg.assistant_mode === 'PRODUCT' ? 'secondary.main' : 'primary.main',
                                display: 'block',
                                mb: 0.5,
                            }}
                        >
                            {msg.assistant_mode === 'PRODUCT' ? 'Manuals & standards' : 'Help centre'}
                        </Typography>
                    )}

                    {/* Loading state with staged progress text */}
                    {msg.isLoading && (
                        <StagedWaitingIndicator question={msg.userQuestion} />
                    )}

                    {/* Error state */}
                    {msg.error && (
                        <Alert severity="error" sx={{ py: 0, px: 1, fontSize: '0.75rem' }}>
                            {msg.error}
                        </Alert>
                    )}

                    {/* Safety Banners */}
                    {!isUser && msg.safety_level === 'STOP_ISOLATE' && (
                        <Alert
                            severity="error"
                            icon={<ErrorOutlineIcon fontSize="inherit" />}
                            data-testid="safety-banner-stop-isolate"
                            sx={{ mb: 1 }}
                        >
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                STOP – isolate before continuing
                            </Typography>
                            <Typography variant="caption">
                                Follow site lock-out / tag-out and verify dead before touching conductors or terminals.
                            </Typography>
                        </Alert>
                    )}

                    {!isUser && msg.safety_level === 'CAUTION' && (
                        <Alert
                            severity="warning"
                            icon={<WarningAmberIcon fontSize="inherit" />}
                            data-testid="safety-banner-caution"
                            sx={{ mb: 1 }}
                        >
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                CAUTION – verify site safety before proceeding
                            </Typography>
                        </Alert>
                    )}

                    {/* Content */}
                    {msg.content && (
                        <Box
                            sx={{
                                fontSize: '0.85rem',
                                lineHeight: 1.55,
                                color: isUser
                                    ? theme.palette.primary.contrastText
                                    : theme.palette.text.primary,
                            }}
                        >
                            <ReactMarkdown
                                disallowedElements={['img']}
                                unwrapDisallowed
                                components={{
                                    p: ({ children }) => (
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontSize: 'inherit',
                                                mb: 1,
                                                '&:last-child': { mb: 0 },
                                            }}
                                        >
                                            {children}
                                        </Typography>
                                    ),
                                    strong: ({ children }) => (
                                        <Box component="strong" sx={{ fontWeight: 600 }}>
                                            {children}
                                        </Box>
                                    ),
                                    code: ({ children }) => (
                                        <Box
                                            component="code"
                                            sx={{
                                                bgcolor: isDark
                                                    ? 'rgba(255,255,255,0.1)'
                                                    : 'rgba(0,0,0,0.05)',
                                                px: 0.5,
                                                borderRadius: 0.5,
                                                fontFamily: 'monospace',
                                                fontSize: '0.85em',
                                            }}
                                        >
                                            {children}
                                        </Box>
                                    ),
                                    ul: ({ children }) => (
                                        <Box component="ul" sx={{ mt: 0.5, mb: 1, pl: 2 }}>
                                            {children}
                                        </Box>
                                    ),
                                    li: ({ children }) => (
                                        <Box component="li" sx={{ mb: 0.5 }}>
                                            <Typography
                                                variant="body2"
                                                sx={{ fontSize: 'inherit' }}
                                            >
                                                {children}
                                            </Typography>
                                        </Box>
                                    ),
                                    a: isV2Assistant
                                        ? ({ children, href }) => {
                                              if (!href) return <>{children}</>;

                                              // In-app links
                                              if (isInAppHref(href)) {
                                                  return (
                                                      <Link
                                                          to={href}
                                                          onClick={() => closePanel?.()}
                                                          style={{
                                                              color: theme.palette.primary.main,
                                                              textDecoration: 'underline',
                                                              fontWeight: 500,
                                                          }}
                                                      >
                                                          {children}
                                                      </Link>
                                                  );
                                              }

                                              // External links: Trade Connector's own site only
                                              if (isAllowedExternalHelpHref(href)) {
                                                  return (
                                                      <a
                                                          href={href}
                                                          target="_blank"
                                                          rel="noopener noreferrer"
                                                          style={{
                                                              color: theme.palette.primary.main,
                                                              textDecoration: 'underline',
                                                          }}
                                                      >
                                                          {children}
                                                      </a>
                                                  );
                                              }

                                              // Any other href renders as plain text
                                              return <>{children}</>;
                                          }
                                        : ({ node: _node, href, children, ...rest }) => {
                                              const { node: _node2, ...restWithoutNode } = rest as Record<string, unknown>;
                                              return (
                                                  <a
                                                      href={href && isSafeAttachmentUrl(href) ? href : '#'}
                                                      target="_blank"
                                                      rel="noopener noreferrer"
                                                      {...restWithoutNode}
                                                  >
                                                      {children}
                                                  </a>
                                              );
                                          },
                                }}
                            >
                                {msg.content}
                            </ReactMarkdown>
                        </Box>
                    )}

                    {/* Conflicts if present */}
                    {!isUser && msg.conflicts && msg.conflicts.length > 0 && (
                        <Alert severity="warning" sx={{ py: 0.5, mt: 1, mb: 1 }}>
                            <Typography
                                variant="caption"
                                sx={{ fontWeight: 600, display: 'block' }}
                            >
                                Source conflict noted:
                            </Typography>
                            {msg.conflicts.map((conflict, idx) => (
                                <Typography
                                    key={idx}
                                    variant="caption"
                                    sx={{ display: 'block' }}
                                >
                                    • {conflict.summary}
                                </Typography>
                            ))}
                        </Alert>
                    )}

                    {/* Source citations via SourceChips */}
                    {msg.sources && msg.sources.length > 0 && (
                        <SourceChips
                            sources={msg.sources}
                            sessionId={msg.expert_session_id || expertSessionId}
                            numbered={msg.version === 2 || Boolean(msg.assistant_mode)}
                        />
                    )}

                    {/* Actions (v2) */}
                    {msg.actions && msg.actions.length > 0 && (
                        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                            {msg.actions.map((action, i) => {
                                if (action.type === 'contact_support') {
                                    return (
                                        <Button
                                            key={i}
                                            variant="outlined"
                                            size="small"
                                            color="primary"
                                            startIcon={<ContactIcon fontSize="small" />}
                                            onClick={() => {
                                                setContactPrefill?.({
                                                    subject: action.prefill?.subject || '',
                                                    message: action.prefill?.message || '',
                                                });
                                                setActiveTab('contact');
                                            }}
                                            sx={{
                                                justifyContent: 'flex-start',
                                                textTransform: 'none',
                                                borderRadius: '8px',
                                                fontSize: '0.75rem',
                                                py: 0.5,
                                                px: 1,
                                            }}
                                        >
                                            {action.label || 'Ask our team'}
                                        </Button>
                                    );
                                }

                                if (action.type === 'open_link') {
                                    return (
                                        <Button
                                            key={i}
                                            variant="outlined"
                                            size="small"
                                            color="secondary"
                                            startIcon={<OpenInNewIcon fontSize="small" />}
                                            onClick={() => {
                                                if (!isInAppHref(action.path)) return;
                                                navigate(action.path);
                                                closePanel?.();
                                            }}
                                            sx={{
                                                justifyContent: 'flex-start',
                                                textTransform: 'none',
                                                borderRadius: '8px',
                                                fontSize: '0.75rem',
                                                py: 0.5,
                                                px: 1,
                                            }}
                                        >
                                            {action.label}
                                        </Button>
                                    );
                                }

                                if (action.type === 'expert_chat') {
                                    if (action.audience === 'TRADE') {
                                        if (hasPermission('platform:view_all')) {
                                            return (
                                                <Button
                                                    key={i}
                                                    variant="outlined"
                                                    size="small"
                                                    color="secondary"
                                                    startIcon={<AIIcon fontSize="small" />}
                                                    onClick={() => {
                                                        navigate(
                                                            `/admin/experts/chat?q=${encodeURIComponent(
                                                                action.question,
                                                            )}`,
                                                        );
                                                        closePanel?.();
                                                    }}
                                                    sx={{
                                                        justifyContent: 'flex-start',
                                                        textTransform: 'none',
                                                        borderRadius: '8px',
                                                        fontSize: '0.75rem',
                                                        py: 0.5,
                                                        px: 1,
                                                    }}
                                                >
                                                    {action.label || 'Ask the Tech Assistant'}
                                                </Button>
                                            );
                                        }

                                        // Trades on web get a short note
                                        return (
                                            <Box
                                                key={i}
                                                sx={{
                                                    p: 1,
                                                    borderRadius: 1,
                                                    bgcolor: theme.palette.action.hover,
                                                    border: `1px solid ${theme.palette.divider}`,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                }}
                                            >
                                                <InfoOutlinedIcon fontSize="small" color="info" />
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ fontSize: '0.75rem' }}
                                                >
                                                    Open the TradeConnector app → Tech Assistant
                                                </Typography>
                                            </Box>
                                        );
                                    }

                                    if (action.audience === 'CLIENT') {
                                        if (isCustomerExpertAvailable) {
                                            return (
                                                <Button
                                                    key={i}
                                                    variant="outlined"
                                                    size="small"
                                                    color="secondary"
                                                    startIcon={<AIIcon fontSize="small" />}
                                                    onClick={() => {
                                                        navigate('/support');
                                                        closePanel?.();
                                                    }}
                                                    sx={{
                                                        justifyContent: 'flex-start',
                                                        textTransform: 'none',
                                                        borderRadius: '8px',
                                                        fontSize: '0.75rem',
                                                        py: 0.5,
                                                        px: 1,
                                                    }}
                                                >
                                                    {action.label || 'Ask about your system'}
                                                </Button>
                                            );
                                        }

                                        // When unavailable: note to message installer from job page, linking to jobs
                                        return (
                                            <Box
                                                key={i}
                                                sx={{
                                                    p: 1,
                                                    borderRadius: 1,
                                                    bgcolor: theme.palette.action.hover,
                                                    border: `1px solid ${theme.palette.divider}`,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    flexWrap: 'wrap',
                                                }}
                                            >
                                                <InfoOutlinedIcon fontSize="small" color="info" />
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ fontSize: '0.75rem' }}
                                                >
                                                    Please message your installer from the job page.
                                                </Typography>
                                                <Button
                                                    size="small"
                                                    variant="text"
                                                    onClick={() => {
                                                        navigate('/dashboard/jobs');
                                                        closePanel?.();
                                                    }}
                                                    sx={{
                                                        fontSize: '0.72rem',
                                                        p: 0,
                                                        minWidth: 0,
                                                        textTransform: 'none',
                                                    }}
                                                >
                                                    Go to Jobs
                                                </Button>
                                            </Box>
                                        );
                                    }
                                }

                                return null;
                            })}
                        </Box>
                    )}

                    {/* Escalation affordance when suggest_escalation is true */}
                    {!isUser && msg.suggest_escalation && !msg.actions?.some((a) => a.type === 'contact_support') && (
                        <Box sx={{ mt: 1 }}>
                            <Button
                                variant="outlined"
                                size="small"
                                color="primary"
                                startIcon={<ContactIcon fontSize="small" />}
                                onClick={() => {
                                    setContactPrefill?.({
                                        subject: 'Escalation from AI Assistant',
                                        message: msg.userQuestion || '',
                                    });
                                    setActiveTab('contact');
                                }}
                                sx={{
                                    justifyContent: 'flex-start',
                                    textTransform: 'none',
                                    borderRadius: '8px',
                                    fontSize: '0.75rem',
                                    py: 0.5,
                                    px: 1,
                                }}
                            >
                                Ask our team
                            </Button>
                        </Box>
                    )}

                    {/* Thumbs up/down feedback (v2) */}
                    {msg.message_id && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                mt: 0.75,
                                pt: 0.5,
                                borderTop: `1px dashed ${alpha(theme.palette.divider, 0.6)}`,
                            }}
                        >
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontSize: '0.7rem', mr: 0.5 }}
                            >
                                Was this helpful?
                            </Typography>
                            <Tooltip title="Helpful">
                                <IconButton
                                    size="small"
                                    onClick={() => handleFeedbackClick(msg.message_id!, 1)}
                                    aria-label="Helpful"
                                    aria-pressed={msg.feedbackRating === 1}
                                    disabled={Boolean(msg.isFeedbackSubmitting)}
                                    color={msg.feedbackRating === 1 ? 'primary' : 'default'}
                                    sx={{
                                        p: 0.5,
                                        bgcolor:
                                            msg.feedbackRating === 1
                                                ? alpha(theme.palette.primary.main, 0.1)
                                                : 'transparent',
                                    }}
                                >
                                    {msg.feedbackRating === 1 ? (
                                        <ThumbUpIcon sx={{ fontSize: '0.9rem' }} />
                                    ) : (
                                        <ThumbUpOutlinedIcon sx={{ fontSize: '0.9rem' }} />
                                    )}
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Not helpful">
                                <IconButton
                                    size="small"
                                    onClick={() => handleFeedbackClick(msg.message_id!, -1)}
                                    aria-label="Not helpful"
                                    aria-pressed={msg.feedbackRating === -1}
                                    disabled={Boolean(msg.isFeedbackSubmitting)}
                                    color={msg.feedbackRating === -1 ? 'error' : 'default'}
                                    sx={{
                                        p: 0.5,
                                        bgcolor:
                                            msg.feedbackRating === -1
                                                ? alpha(theme.palette.error.main, 0.1)
                                                : 'transparent',
                                    }}
                                >
                                    {msg.feedbackRating === -1 ? (
                                        <ThumbDownIcon sx={{ fontSize: '0.9rem' }} />
                                    ) : (
                                        <ThumbDownOutlinedIcon sx={{ fontSize: '0.9rem' }} />
                                    )}
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}
                </Box>
            </Box>
        );
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 1 }}>
            {/* ── Brand / Equipment Context Chip ── */}
            {equipmentScope && (
                <Box
                    sx={{
                        px: 1,
                        py: 0.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: 1,
                        borderColor: 'divider',
                        bgcolor: alpha(theme.palette.secondary.main, 0.04),
                        borderRadius: 1,
                    }}
                >
                    <EquipmentBrandChip
                        equipmentScope={equipmentScope}
                        expertSessionId={expertSessionId}
                        onEquipmentChanged={(nextScope) => {
                            const prevMfrId = equipmentScope?.manufacturer_id ?? null;
                            const nextMfrId = nextScope?.manufacturer_id ?? null;
                            if (nextMfrId !== prevMfrId) {
                                setFlowOffers([]);
                            }
                            setEquipmentScope(nextScope);
                        }}
                    />
                </Box>
            )}

            {/* ── Chat Messages ── */}
            <Box
                sx={{
                    flex: 1,
                    overflow: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                }}
            >
                {messages.length === 0 ? (
                    /* ── Empty state ── */
                    <Box
                        sx={{
                            textAlign: 'center',
                            py: 3,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 1.5,
                        }}
                    >
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifySelf: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <AIIcon sx={{ color: '#fff', fontSize: '1.5rem' }} />
                        </Box>
                        <Box>
                            <Typography variant="body2" fontWeight={600}>
                                Hi! I'm your AI assistant
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Ask me anything about{' '}
                                {location.pathname.includes('/jobs') ? 'jobs' : 'Trade Connector, manuals or standards'}
                            </Typography>
                        </Box>

                        {/* Quick suggestions based on user role */}
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 0.5,
                                justifyContent: 'center',
                                mt: 1,
                            }}
                        >
                            {starters.map((s) => (
                                <Chip
                                    key={s}
                                    label={s}
                                    size="small"
                                    clickable
                                    onClick={() => handleSuggestionClick(s)}
                                    sx={{
                                        fontSize: '0.72rem',
                                        height: 28,
                                        bgcolor: alpha(theme.palette.secondary.main, 0.08),
                                        borderColor: alpha(theme.palette.secondary.main, 0.2),
                                        '&:hover': {
                                            bgcolor: alpha(theme.palette.secondary.main, 0.18),
                                        },
                                    }}
                                    variant="outlined"
                                />
                            ))}
                        </Box>
                    </Box>
                ) : (
                    /* ── Messages list ── */
                    <Box sx={{ px: 0.5 }}>
                        {messages.map(renderMessage)}

                        {/* Troubleshooting Flow Error (L1) */}
                        {flow.flowError && (
                            <Alert
                                severity="error"
                                sx={{ my: 1, py: 0.5, px: 1, fontSize: '0.8rem' }}
                                onClose={() => flow.dismissFlow()}
                            >
                                {flow.flowError}
                            </Alert>
                        )}

                        {/* Troubleshooting Flow Card */}
                        {(flow.activeFlowTerminal || flow.activeFlowNode || flowOffer) && (
                            <Box sx={{ my: 1 }}>
                                <TroubleshootingFlowCard
                                    offer={flowOffer}
                                    activeRun={flow.activeFlowRun}
                                    activeNode={flow.activeFlowNode}
                                    activeTerminal={flow.activeFlowTerminal}
                                    flowTitle={flow.flowTitle || flow.activeFlowRun?.flow_title || flowOffer?.title}
                                    sessionId={expertSessionId}
                                    isSubmitting={flow.isFlowLoading}
                                    needsClarification={flow.needsClarification}
                                    onStart={(flowId) => void flow.startFlow(flowId, flowOffer?.title)}
                                    onDismiss={(flowId) => {
                                        const hasStartedFlow = Boolean(
                                            flow.activeFlowTerminal || flow.activeFlowNode || flow.activeFlowRun,
                                        );
                                        flow.dismissFlow();
                                        if (!hasStartedFlow) {
                                            dismissFlow(flowId);
                                        }
                                    }}
                                    onAnswer={(payload) => void flow.answerFlow(payload)}
                                    onBack={() => void flow.stepBackFlow()}
                                    onExit={() => void flow.abandonFlow()}
                                    onFetchSummary={flow.fetchFlowSummary}
                                />
                            </Box>
                        )}

                        <div ref={messagesEndRef} />
                    </Box>
                )}
            </Box>

            {/* ── Escalation link ── */}
            {(messages.length > 2 || messages.some((m) => m.suggest_escalation)) && (
                <Box
                    sx={{
                        textAlign: 'center',
                        py: 0.5,
                    }}
                >
                    <Chip
                        icon={<ContactIcon sx={{ fontSize: '0.85rem !important' }} />}
                        label="Can't find what you need? Contact us"
                        size="small"
                        clickable
                        onClick={() => setActiveTab('contact')}
                        variant="outlined"
                        sx={{ fontSize: '0.72rem', height: 26 }}
                    />
                </Box>
            )}

            {/* ── Input area ── */}
            <Box
                sx={{
                    display: 'flex',
                    gap: 0.5,
                    alignItems: 'flex-end',
                    borderTop: 1,
                    borderColor: 'divider',
                    pt: 1,
                }}
            >
                {messages.length > 0 && (
                    <Tooltip title="Clear chat">
                        <IconButton
                            size="small"
                            onClick={() => {
                                clearMessages();
                                flow.dismissFlow();
                            }}
                            aria-label="Clear chat"
                            sx={{ color: 'text.secondary', mb: 0.25 }}
                        >
                            <ClearIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                <TextField
                    inputRef={inputRef}
                    fullWidth
                    size="small"
                    placeholder="Ask me anything..."
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={isLoading}
                    autoComplete="off"
                    multiline
                    maxRows={3}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <Tooltip title="Send message">
                                    <span>
                                        <IconButton
                                            size="small"
                                            onClick={handleSend}
                                            disabled={!inputValue.trim() || isLoading}
                                            color="secondary"
                                            aria-label="Send message"
                                        >
                                            {isLoading ? (
                                                <CircularProgress size={18} />
                                            ) : (
                                                <SendIcon fontSize="small" />
                                            )}
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '12px',
                            fontSize: '0.85rem',
                        },
                    }}
                />
            </Box>
        </Box>
    );
};

export default AIAssistantTab;
